import React, { useState } from "react";
import "./Checkout.css";
import Header from "../../Components/Shared/Header/Header";
import Footer from "../../Components/Shared/Footer/Footer";
import VivahApi from "../../api";
import { useLocation, useNavigate } from "react-router-dom";

const DEFAULT_FEATURES = [
  "Send & receive interests",
  "View full profiles",
  "Direct messaging",
  "Priority support",
];

function loadRazorpaySDK() {
  return new Promise((resolve) => {
    if (window.Razorpay) { resolve(true); return; }
    const s = document.createElement("script");
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.onload  = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });
}

function Checkout() {
  const location = useLocation();
  const navigate  = useNavigate();
  const plan      = location.state?.plan;

  const [status,   setStatus]   = useState("idle");   // idle | paying | success | failed
  const [result,   setResult]   = useState(null);
  const [errMsg,   setErrMsg]   = useState("");
  const [loading,  setLoading]  = useState(false);

  if (!plan) {
    return (
      <>
        <div className="checkout-page">
          <Header />
          <div className="checkout-card">
            <div className="checkout-card__body">
              <p style={{ textAlign: "center", color: "#e53935" }}>
                No plan selected. <button className="checkout-back" onClick={() => navigate("/update")}>Browse Plans</button>
              </p>
            </div>
          </div>
        </div>
      </>
    );
  }

  const features = plan.features?.length ? plan.features : DEFAULT_FEATURES;

  const handlePay = async () => {
    setLoading(true);
    setErrMsg("");

    // 1 — Load Razorpay SDK
    const sdkLoaded = await loadRazorpaySDK();
    if (!sdkLoaded) {
      setErrMsg("Razorpay SDK failed to load. Please check your connection.");
      setLoading(false);
      return;
    }

    // 2 — Create order on backend
    let orderData;
    try {
      const res = await VivahApi.Payment.createOrderId(plan.planId);
      if (res.data?.error) {
        setErrMsg(res.data.error);
        setLoading(false);
        return;
      }
      orderData = res.data;
    } catch (err) {
      const status = err?.response?.status;
      const serverMsg = err?.response?.data?.error || err?.response?.data?.message;
      console.error("create-order error:", status, err?.response?.data);
      setErrMsg(serverMsg || `Failed to create order (HTTP ${status || "network error"}). Please try again.`);
      setLoading(false);
      return;
    }

    const { orderId, transactionId, amount, currency, name, email, phone, keyId } = orderData;

    // 3 — Open Razorpay modal
    setLoading(false);
    setStatus("paying");

    const options = {
      key:      keyId,
      amount,
      currency: currency || "INR",
      name:     "Vivah Bandh",
      description: plan.name,
      order_id: orderId,
      prefill:  { name, email, contact: phone },
      theme:    { color: "#e91e63" },
      modal: {
        ondismiss: () => setStatus("idle"),
      },
      handler: async (response) => {
        // 4 — Verify payment
        console.log("[Checkout] Razorpay handler called:", {
          orderId:   response.razorpay_order_id,
          paymentId: response.razorpay_payment_id,
          signature: response.razorpay_signature ? "present" : "MISSING",
          transactionId,
        });
        try {
          const verifyRes = await VivahApi.Payment.verifyPayment({
            razorpayOrderId:    response.razorpay_order_id,
            razorpayPaymentId:  response.razorpay_payment_id,
            razorpaySignature:  response.razorpay_signature,
            transactionId,
          });
          console.log("[Checkout] verify response:", verifyRes.data);
          if (verifyRes.data?.success) {
            setResult(verifyRes.data);
            localStorage.setItem("planName",   verifyRes.data.planName  || "Premium");
            localStorage.setItem("planExpiry", verifyRes.data.expiresOn || "");
            localStorage.setItem("planId",     verifyRes.data.planId    || "");
            setStatus("success");
          } else {
            console.warn("[Checkout] verify returned non-success:", verifyRes.data);
            setErrMsg(verifyRes.data?.error || "Payment verification failed.");
            setStatus("failed");
          }
        } catch (err) {
          console.error("[Checkout] verify error:", err?.response?.status, err?.response?.data, err);
          setErrMsg(err?.response?.data?.error || `Verification error (${err?.response?.status || err?.message}). Contact support.`);
          setStatus("failed");
        }
      },
    };

    const rzp = new window.Razorpay(options);
    rzp.on("payment.failed", (response) => {
      setErrMsg(response.error?.description || "Payment failed.");
      setStatus("failed");
    });
    rzp.open();
  };

  // ── Success screen ───────────────────────────────────────────
  if (status === "success") {
    return (
      <>
        <div className="checkout-page">
          <Header />
          <div className="checkout-card">
            <div className="checkout-card__body">
              <div className="checkout-result">
                <div className="checkout-result__icon">🎉</div>
                <div className="checkout-result__title">Payment Successful!</div>
                <div className="checkout-result__sub">
                  Your <strong>{result?.planName || plan.name}</strong> plan is now active.
                  Expires on <strong>{result?.expiresOn}</strong>.
                </div>
                <button
                  className="checkout-result__btn"
                  onClick={() => navigate("/my-profile")}
                >
                  Go to My Profile
                </button>
              </div>
            </div>
          </div>
          <Footer />
        </div>
      </>
    );
  }

  // ── Failed screen ────────────────────────────────────────────
  if (status === "failed") {
    return (
      <>
        <div className="checkout-page">
          <Header />
          <div className="checkout-card">
            <div className="checkout-card__body">
              <div className="checkout-result">
                <div className="checkout-result__icon">❌</div>
                <div className="checkout-result__title">Payment Failed</div>
                <div className="checkout-result__sub">{errMsg || "Something went wrong."}</div>
                <button
                  className="checkout-result__btn"
                  style={{ background: "#fff", color: "#e91e63", border: "2px solid #e91e63", boxShadow: "none" }}
                  onClick={() => { setStatus("idle"); setErrMsg(""); }}
                >
                  Try Again
                </button>
              </div>
            </div>
          </div>
          <Footer />
        </div>
      </>
    );
  }

  // ── Default screen ───────────────────────────────────────────
  return (
    <>
      <div className="checkout-page">
        <Header />
        <div className="checkout-card">
          {/* Header strip */}
          <div className="checkout-card__header">
            <h2>Complete Your Purchase</h2>
            <p>Secure payment powered by Razorpay</p>
          </div>

          <div className="checkout-card__body">
            {/* Plan summary */}
            <div className="checkout-summary">
              <span className="checkout-summary__icon">
                {plan.name?.toLowerCase().includes("silver") ? "🥈"
                  : plan.name?.toLowerCase().includes("gold")    ? "🥇"
                  : plan.name?.toLowerCase().includes("diamond")  ? "💎"
                  : plan.name?.toLowerCase().includes("platinum") ? "👑"
                  : "🎁"}
              </span>
              <div className="checkout-summary__info">
                <div className="checkout-summary__name">{plan.name}</div>
                <div className="checkout-summary__duration">
                  {plan.durationDays} days · {plan.durationLabel || ""}
                </div>
              </div>
              <div className="checkout-summary__price">
                ₹{plan.price?.toLocaleString("en-IN")}
              </div>
            </div>

            {/* Features */}
            <ul className="checkout-features">
              {features.map((f, i) => <li key={i}>{f}</li>)}
            </ul>

            {/* Error */}
            {errMsg && (
              <div style={{ color: "#e53935", fontSize: 13, background: "#fce4ec", padding: "10px 14px", borderRadius: 8 }}>
                {errMsg}
              </div>
            )}

            {/* Pay button */}
            <button
              className="checkout-btn"
              onClick={handlePay}
              disabled={loading || status === "paying"}
            >
              {loading ? "Processing…" : `Pay ₹${plan.price?.toLocaleString("en-IN")}`}
            </button>

            {/* Secure badge */}
            <div className="checkout-secure">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="#aaa">
                <path d="M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4z"/>
              </svg>
              Payments are 100% secure &amp; encrypted
            </div>

            <button className="checkout-back" onClick={() => navigate("/update")}>
              ← Back to Plans
            </button>
          </div>
        </div>
        <Footer />
      </div>
    </>
  );
}

export default Checkout;
